/**
 * DaysInsightsReport — renders the full Day's Insights structure exactly
 * matching the PDF the user shared:
 *   • Child's Name / Date / Curiosity Seed (simple header lines)
 *   • Growth Metrics & Observations (3-column table)
 *   • Curiosity Response Index (score + analysis)
 *   • Disposition During the Session + Engagement Score badge beside it
 *   • Communication Skills & Thought Clarity
 *   • Overall Growth Score (Balanced/Moderate/Limited)
 *   • Note for Parent
 *   • Legend (Performance by Area + Overall Growth Score)
 *
 * Engagement Score is rendered AS A BADGE next to the Disposition heading,
 * with a colored 0-100 number and a label (Highly/Actively/Moderately/etc).
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import TextReport from './TextReport';

const RATING_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  needs_work: 'Needs Work',
};
const RATING_EMOJIS = {
  excellent: '🟢', good: '✅', fair: '⚠️', needs_work: '📈',
};
const AREA_EMOJIS = {
  intellectual: '🧠', emotional: '😊', social: '🤝', creative: '🎨',
  physical: '🏃', 'planning/independence': '🚀', 'planning & independence': '🚀',
  character: '🧭',
};
const ratingColor = (r) => ({
  excellent: 'text-green-700', good: 'text-green-600',
  fair: 'text-amber-600', needs_work: 'text-orange-600',
}[r?.toLowerCase()] || 'text-slate-700');

const overallEmoji = (r) => ({
  balanced: '🔵', moderate: '🟡', limited: '🔴',
  excellent: '🔵', good: '🔵', fair: '🟡', needs_work: '🔴',
}[r?.toLowerCase()] || '⚪');
const overallLabel = (r) => ({
  balanced: 'Balanced Growth', moderate: 'Moderate Growth', limited: 'Limited Growth',
  excellent: 'Balanced Growth', good: 'Balanced Growth',
  fair: 'Moderate Growth', needs_work: 'Limited Growth',
}[r?.toLowerCase()] || (r || 'Pending'));

const engagementColor = (score) => {
  if (score >= 80) return 'bg-green-100 text-green-800 border-green-300';
  if (score >= 60) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (score >= 40) return 'bg-amber-100 text-amber-800 border-amber-300';
  if (score >= 20) return 'bg-orange-100 text-orange-800 border-orange-300';
  return 'bg-red-100 text-red-800 border-red-300';
};

const DaysInsightsReport = ({ data, title = "Day's Insights" }) => {
  if (!data) return null;

  if (data.report_text) {
    return <TextReport text={data.report_text} title={title} />;
  }

  const engagement = data.engagement_score || {};
  const ogs = data.overall_growth_score || {};

  return (
    <div className="space-y-6" data-testid="days-insights-report">
      {/* HEADER */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            📋 {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p><span className="text-base">🧒</span> <strong>Child's Name:</strong> {data.child_name || '—'}</p>
          <p><span className="text-base">📅</span> <strong>Date:</strong> {data.date || '—'}</p>
          <p><span className="text-base">🌱</span> <strong>Curiosity Seed Explored:</strong> {data.curiosity_seed || '—'}</p>
        </CardContent>
      </Card>

      {/* GROWTH METRICS */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">📊 Growth Metrics & Observations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="growth-metrics-table">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-3 py-2 font-semibold w-1/4">Growth Area</th>
                  <th className="px-3 py-2 font-semibold w-1/6">Rating</th>
                  <th className="px-3 py-2 font-semibold">Observation Summary</th>
                </tr>
              </thead>
              <tbody>
                {(data.growth_metrics || []).map((m, idx) => {
                  const r = (m.rating || '').toLowerCase();
                  const areaKey = (m.area || '').toLowerCase();
                  return (
                    <tr key={idx} className="border-t align-top">
                      <td className="px-3 py-3">
                        <span className="mr-1.5">{AREA_EMOJIS[areaKey] || '📌'}</span>
                        {m.area}
                      </td>
                      <td className={`px-3 py-3 font-semibold ${ratingColor(r)}`}>
                        <span className="mr-1">{RATING_EMOJIS[r] || ''}</span>
                        {RATING_LABELS[r] || m.rating}
                      </td>
                      <td className="px-3 py-3 text-slate-700 leading-relaxed">
                        {m.observation}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* CURIOSITY RESPONSE INDEX */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">🌈 Curiosity Response Index</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-semibold mb-2 text-violet-700 text-xl">
            Score: {data.curiosity_response_index?.score ?? '—'} / {data.curiosity_response_index?.max_score ?? 10}
          </p>
          <p className="text-slate-700 leading-relaxed">{data.curiosity_response_index?.analysis}</p>
        </CardContent>
      </Card>

      {/* DISPOSITION + ENGAGEMENT BADGE */}
      {data.disposition && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg">🎭 Disposition During the Session</CardTitle>
              {engagement.score !== undefined && (
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium ${engagementColor(engagement.score)}`}
                  data-testid="engagement-score-badge"
                >
                  <span className="text-base">⚡</span>
                  <span>Engagement <strong>{engagement.score}</strong>/100</span>
                  <span className="text-xs opacity-80">· {engagement.label}</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 leading-relaxed">{data.disposition}</p>
          </CardContent>
        </Card>
      )}

      {/* COMMUNICATION SKILLS */}
      {data.communication_skills && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">🗣️ Communication Skills & Thought Clarity</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 leading-relaxed">{data.communication_skills}</p>
          </CardContent>
        </Card>
      )}

      {/* OVERALL GROWTH SCORE */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">🧠 Overall Growth Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-3">
            <span className="text-3xl">{overallEmoji(ogs.rating)}</span>
            <div>
              <p className="font-bold text-xl">{overallLabel(ogs.rating)}</p>
              <p className="text-slate-500 text-sm">{ogs.areas_active || 0}/{ogs.total_areas || 7} Areas Active</p>
            </div>
          </div>
          <p className="text-slate-700 leading-relaxed">{ogs.summary}</p>
        </CardContent>
      </Card>

      {/* NOTE FOR PARENT */}
      {data.note_for_parent && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">📣 Note for Parent</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 leading-relaxed bg-violet-50 p-4 rounded-lg border-l-4 border-violet-400">
              {data.note_for_parent}
            </p>
          </CardContent>
        </Card>
      )}

      {/* LEGEND */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">🟢 Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-medium mb-2">✅ Performance by Area</p>
          <div className="grid md:grid-cols-2 gap-2 text-sm mb-4">
            <div>🟢 <strong>Excellent</strong> (7/7 areas) – Clear growth with strong evidence</div>
            <div>💚 <strong>Good</strong> (5-6 areas) – Solid engagement with positive trends</div>
            <div>⚠️ <strong>Fair</strong> (3-4 areas) – Some engagement, needs encouragement</div>
            <div>📈 <strong>Needs Work</strong> (1-2 areas) – Area not activated or underperforming</div>
          </div>
          <p className="font-medium mb-2">🧠 Overall Growth Score</p>
          <div className="grid md:grid-cols-3 gap-2 text-sm">
            <div>🔵 <strong>Balanced Growth</strong> – 6-7 areas active</div>
            <div>🟡 <strong>Moderate Growth</strong> – 4-5 areas active</div>
            <div>🔴 <strong>Limited Growth</strong> – 1-3 areas active</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DaysInsightsReport;
